import React, { useEffect, useRef } from 'react';
import { Modal, Descriptions, Alert, Image, Divider, Tag } from 'antd';

import { useCurrentTrip } from '../hooks/useCurrentTrip';
import { getEndIcon, getRiderIcon, getStartIcon, getTaxiIcon } from '../../../shared/Utils';

const TripsViewModal = ({ isOpen, onRequestClose }) => {
    const { currentTrip } = useCurrentTrip();

    const mapContainerRef = useRef(null);

    const handleCancel = () => {
        onRequestClose(false);
    };

    useEffect(() => {
        if (!isOpen || !currentTrip) return;

        const initializeMap = () => {
            if (!mapContainerRef.current) return;

            const start = new window.google.maps.LatLng(
                currentTrip.startLocation.coordinates.latitude,
                currentTrip.startLocation.coordinates.longitude
            );

            const end = new window.google.maps.LatLng(
                currentTrip.endLocation.coordinates.latitude,
                currentTrip.endLocation.coordinates.longitude
            );

            const map = new window.google.maps.Map(mapContainerRef.current, {
                zoom: 13,
                center: start,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
            });

            const bounds = new window.google.maps.LatLngBounds();

            bounds.extend(start);
            bounds.extend(end);

            // Pickup marker
            new window.google.maps.Marker({
                position: start,
                map,
                label: 'Start Location',
                title: 'Pickup',
                icon: getStartIcon(false),
            });

            // Destination marker
            new window.google.maps.Marker({
                position: end,
                map,
                label: 'End Location',
                title: 'Dropoff',
                icon: getEndIcon(false),
            });

            // Driver marker
            const driverLocation = currentTrip?.taxiOfferId?.taxiDriver?.currentLocation;

            if (driverLocation?.latitude && driverLocation?.longitude) {
                const driver = currentTrip.taxiOfferId.taxiDriver;

                const driverLatLng = new window.google.maps.LatLng(driverLocation.latitude, driverLocation.longitude);

                const driverMarker = new window.google.maps.Marker({
                    position: driverLatLng,
                    map,
                    title: driver.fullName,
                    icon: getTaxiIcon(false),
                });

                const infoWindow = new window.google.maps.InfoWindow();

                driverMarker.addListener('click', () => {
                    infoWindow.setContent(`
                    <div style="min-width:220px">
                        <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
                            <img
                                src="${driver?.driverPhoto?.url || '/assets/user.png'}"
                                width="50"
                                height="50"
                                style="border-radius:50%;object-fit:cover;"
                            />

                            <div>
                                <strong>${driver.fullName}</strong><br/>
                                ${driver.phoneNumber || ''}
                            </div>
                        </div>

                        <hr/>

                        <p><strong>Email:</strong> ${driver.email || '-'}</p>
                        <p><strong>Immatriculation:</strong> ${driver.immatriculation || '-'}</p>
                        <p><strong>Status:</strong> ${driver.isDriverOnline ? 'Online' : 'Offline'}</p>
                    </div>
                `);

                    infoWindow.open({
                        anchor: driverMarker,
                        map,
                    });
                });

                bounds.extend(driverLatLng);
            }

            // Rider marker
            const riderLocation = currentTrip?.passengerId?.currentLocation;

            if (riderLocation?.latitude && riderLocation?.longitude) {
                const rider = currentTrip.passengerId;

                const riderLatLng = new google.maps.LatLng(riderLocation.latitude, riderLocation.longitude);

                const riderMarker = new google.maps.Marker({
                    position: riderLatLng,
                    map,
                    title: rider.fullName,
                    icon: getRiderIcon(false),
                });

                const infoWindow = new google.maps.InfoWindow();

                riderMarker.addListener('click', () => {
                    infoWindow.setContent(`
                        <div style="min-width:220px">
                            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
                                <img
                                    src="${rider?.riderPhoto?.url || '/assets/user.png'}"
                                    width="50"
                                    height="50"
                                    style="border-radius:50%;object-fit:cover;"
                                />

                                <div>
                                    <strong>${rider.fullName || 'Rider'}</strong><br/>
                                    ${rider.phoneNumber || ''}
                                </div>
                            </div>

                            <hr/>

                            <p><strong>Email:</strong> ${rider.email || '-'}</p>
                            <p><strong>Gender:</strong> ${rider.gender || '-'}</p>

                            <p>
                                <strong>Location:</strong>
                                ${riderLocation.latitude.toFixed(5)},
                                ${riderLocation.longitude.toFixed(5)}
                            </p>
                        </div>
                    `);

                    infoWindow.open({
                        anchor: riderMarker,
                        map,
                    });
                });

                bounds.extend(riderLatLng);
            }

            // Draw route
            const directionsService = new window.google.maps.DirectionsService();

            const directionsRenderer = new window.google.maps.DirectionsRenderer({
                suppressMarkers: true,
                polylineOptions: {
                    strokeColor: '#1677ff',
                    strokeWeight: 6,
                },
            });

            directionsRenderer.setMap(map);

            directionsService.route(
                {
                    origin: start,
                    destination: end,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                },
                (result, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK) {
                        directionsRenderer.setDirections(result);
                    }
                }
            );

            map.fitBounds(bounds);
        };

        if (window.google?.maps) {
            initializeMap();
            return;
        }

        const existingScript = document.getElementById('google-maps-script');

        if (existingScript) {
            existingScript.addEventListener('load', initializeMap);
            return;
        }

        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = initializeMap;

        document.body.appendChild(script);
    }, [currentTrip, isOpen]);

    return (
        <Modal open={isOpen} onCancel={handleCancel} footer={null} width={900}>
            <h2 style={{ marginBottom: 20 }}>Trip #{currentTrip?.logicalId}</h2>

            {!['Closed', 'Canceled'].includes(currentTrip?.status) && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 20 }}
                    message="Trip is still in Open or In Progress"
                    description="The map shows the planned route along with the current locations of the rider and driver."
                />
            )}

            {currentTrip?.status === 'Closed' && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 20 }}
                    message="Trip is Terminated"
                    description="The map shows the planned route along with the current locations of the rider and driver."
                />
            )}

            <Descriptions title="Trip Information" bordered column={2}>
                <Descriptions.Item label="Status">
                    <Tag color="blue">{currentTrip?.status}</Tag>
                </Descriptions.Item>

                {currentTrip?.taxiOfferId?.offerPrice && (
                    <Descriptions.Item label="Offered Price">
                        {currentTrip?.taxiOfferId?.offerPrice} DT
                    </Descriptions.Item>
                )}

                <Descriptions.Item label="Budget">{currentTrip?.budget} DT</Descriptions.Item>

                <Descriptions.Item label="Recommended Price">{currentTrip?.recommendedPrice} DT</Descriptions.Item>

                <Descriptions.Item label="Distance">{currentTrip?.distanceStartToEnd} km</Descriptions.Item>

                <Descriptions.Item label="Duration">{currentTrip?.tripDuration} min</Descriptions.Item>

                <Descriptions.Item label="Pickup" span={2}>
                    {currentTrip?.startLocation?.label}
                </Descriptions.Item>

                <Descriptions.Item label="Destination" span={2}>
                    {currentTrip?.endLocation?.label}
                </Descriptions.Item>

                <Descriptions.Item label="Note" span={2}>
                    {currentTrip?.note}
                </Descriptions.Item>
            </Descriptions>

            <div
                ref={mapContainerRef}
                style={{
                    height: 420,
                    width: '100%',
                    marginTop: 10,
                    borderRadius: 10,
                    overflow: 'hidden',
                    border: '1px solid #ddd',
                }}
            />
        </Modal>
    );
};

export default TripsViewModal;
